import {
  ALL_ASSET_CLASSES,
  type ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields,
  type AssetClass,
  type SortDirection,
} from '@vizit/api';

type CollectionImageSortField = ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields;

/**
 * The score field a collection's images are sorted by. Resolves to `'vizit_score'` today;
 * widens to include `'hero_score'` automatically once the API exposes it as a sortable field
 * (after `pnpm generate`), so hero collections can sort by the hero score shown on their cards.
 */
type CollectionImageScoreField = Extract<CollectionImageSortField, 'vizit_score' | 'hero_score'>;

enum CollectionAssetSortOption {
  ScoreDesc = 'score_desc',
  ScoreAsc = 'score_asc',
  DateNewest = 'date_newest',
}

const DEFAULT_ASSET_SORT = CollectionAssetSortOption.DateNewest;

const DEFAULT_SCORE_FIELD: CollectionImageScoreField = 'vizit_score';

function mapSortOptionToQueryParams(
  option: CollectionAssetSortOption,
  scoreField: CollectionImageScoreField,
): { sort_by: CollectionImageSortField; sort_direction: SortDirection } {
  switch (option) {
    case CollectionAssetSortOption.ScoreDesc:
      return { sort_by: scoreField, sort_direction: 'desc' };
    case CollectionAssetSortOption.ScoreAsc:
      return { sort_by: scoreField, sort_direction: 'asc' };
    default:
      return { sort_by: 'created_at', sort_direction: 'desc' };
  }
}

function parseCollectionAssetSort(value: unknown): CollectionAssetSortOption | undefined {
  const option = value as CollectionAssetSortOption;
  // The default sort is represented by an absent param, so it never persists in the URL.
  return option !== DEFAULT_ASSET_SORT && (Object.values(CollectionAssetSortOption) as string[]).includes(option)
    ? option
    : undefined;
}

function parseSparkHidden(value: unknown): boolean {
  return value === 'hidden';
}

function parseFavoritesOnly(value: unknown): boolean {
  return value === 'only';
}

/**
 * Parses the asset-class filter from the URL. Returns the class when it is a recognized
 * `AssetClass`, otherwise `undefined` (no filter). Absent/invalid values never persist.
 *
 * Validates against `ALL_ASSET_CLASSES` — the full union, including `Hero Image` — rather
 * than the selectable subset, since a hero collection can legitimately be filtered by it.
 * The picker's options come from the API (the classes actually present in the collection),
 * never from this list.
 */
function parseCollectionClassification(value: unknown): AssetClass | undefined {
  return typeof value === 'string' && (ALL_ASSET_CLASSES as readonly string[]).includes(value)
    ? (value as AssetClass)
    : undefined;
}

/**
 * Maps the selected asset class to the API's repeatable `classification` filter param.
 * `null`/`undefined` (i.e. "All asset classes") clears the filter.
 */
function mapClassificationToQueryParam(classification?: AssetClass | null): AssetClass[] | undefined {
  return classification ? [classification] : undefined;
}

/**
 * Reconciles a URL-supplied asset class against the classes actually present in the collection.
 *
 * `parseCollectionClassification` only proves a URL value is a real `AssetClass`, not that this
 * collection contains it — so a stale or shared link (e.g. `?assetClass=Lifestyle` on a collection
 * with no Lifestyle images) would filter the grid to empty while the picker is hidden, leaving no
 * way to clear it. This collapses such a value to "All" (`undefined`).
 *
 * While `availableClasses` is still loading (`isResolved` false) the URL value is trusted, so a
 * valid filter isn't dropped — and the grid isn't briefly refetched unfiltered — mid-load.
 */
function resolveActiveClassification(
  classification: AssetClass | undefined,
  availableClasses: AssetClass[],
  isResolved: boolean,
): AssetClass | undefined {
  if (!isResolved) {
    return classification;
  }
  return classification && availableClasses.includes(classification) ? classification : undefined;
}

export {
  CollectionAssetSortOption,
  DEFAULT_ASSET_SORT,
  DEFAULT_SCORE_FIELD,
  mapClassificationToQueryParam,
  mapSortOptionToQueryParams,
  parseCollectionAssetSort,
  parseCollectionClassification,
  parseFavoritesOnly,
  parseSparkHidden,
  resolveActiveClassification,
};
export type { CollectionImageScoreField };
